import React, { useEffect, useState } from "react";
import instance from "../../../../axios/instance";
import { getToken } from "../../../../utils/token";
import {
  Outlet,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import {
  ArrowLeft2,
  ArrowRight2,
  Edit,
  Refresh,
  SearchNormal,
  Sort,
} from "iconsax-react";
import {
  NewOrder,
  RefillOrder,
} from "../../../../components/order_types/order_types";
import CreateOrder from "../../../../components/modals/order/CreateOrder";
import { PaginationButton } from "../../../../components/PaginationButton";
import { Tooltip } from "react-tooltip";
import { CheckCheck } from "lucide-react";

export const orderLoader = async ({ params }) => {
  const token = getToken();
  const orders = await instance.post(
    "/doctor/order/all",
    {
      patientId: params.id,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return { orders: orders.data, id: params.id };
};

const OrderPage = () => {
  let { orders: _orders, id } = useLoaderData();
  const [orders, setOrders] = useState(
    _orders.map((_, index) => ({ ..._, index: index + 1 }))
  );
  const [filteredOrders, setFilteredOrders] = useState(
    orders.filter((_, index) => index + 1 <= 10)
  );
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredOrders(() => {
      return orders.filter((order) => {
        const id = `${order.id}`;

        return query ? id === query : order;
      });
    });
  };

  useEffect(() => {
    setFilteredOrders(() =>
      orders.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );
  }, [pageIndex, orders]);

  useEffect(() => {
    setOrders(_orders.map((_, index) => ({ ..._, index: index + 1 })));
  }, [_orders]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }

        orders.reverse();
        setOrders(orders);
        setFilteredOrders((items) =>
          orders.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "type": {
        setPageIndex(1);
        if (sorted === "type") {
          setSorted("");
          orders.sort((a, b) => a.index - b.index);
        } else {
          setSorted("type");
          orders.sort((a, b) => {
            if (a.order_type < b.order_type) return -1;
            if (a.order_type > b.order_type) return 1;
            return 0;
          });
        }
        setFilteredOrders((items) =>
          orders.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "status": {
        setPageIndex(1);
        if (sorted === "status") {
          setSorted("");
          orders.sort((a, b) => a.index - b.index);
        } else {
          setSorted("status");
          orders.sort((a, b) => {
            if (a.order_status < b.order_status) return -1;
            if (a.order_status > b.order_status) return 1;
            return 0;
          });
        }
        setFilteredOrders((items) =>
          orders.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
      case "date": {
        setPageIndex(1);
        if (sorted === "date") {
          setSorted("");
          orders.sort((a, b) => a.index - b.index);
        } else {
          setSorted("date");
          orders.sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
        }
        setFilteredOrders((items) =>
          orders.filter((_, index) => index + 1 <= 10)
        );
        return;
      }
    }
  };

  return (
    <>
    <div className="px-7 py-10">
      {isCreateOrderModalOpen && (
        <CreateOrder
          isOpen={isCreateOrderModalOpen}
          patientId={id}
          onClose={() => setIsCreateOrderModalOpen(false)}
          triggerRevalidate={() => {
            revalidate();
          }}
        />
      )}
      <div className="flex justify-between items-center">
        <div className="font-bold text-[16px]">
          Patient Orders ({orders.length})
        </div>

        <div className="flex gap-2 items-center">
          <button
            className="bg-[#00b0ad0d] py-2 px-3 text-primary flex gap-2 items-center rounded-[4px] font-medium text-[14px]"
            onClick={() => location.reload()}
          >
            Refresh <Refresh size={16} />
          </button>
          <button
            className="bg-primary py-2 px-3 shadow-sm rounded-[4px] font-medium text-[14px] text-white"
            onClick={() => setIsCreateOrderModalOpen(true)}
          >
            Create Order +
          </button>
        </div>
      </div>

      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearch}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder placeholder:text-[14px] text-sm"
              placeholder="Search order by ID"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(orders.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("index")}
                >
                  #{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "index" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">Order ID</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("type")}
                >
                  Type{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "type" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="intake">
                  I
                </td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="pv1">
                  PV1
                </td>
                <td
                  className="w-[40px] px-1 py-2"
                  data-tooltip-id="fulfillment"
                >
                  F
                </td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="pv2">
                  PV2
                </td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="shipping">
                  S
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("date")}
                >
                  Date{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "date" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td></td>
              </tr>
            </thead>

            <tbody>
              {orders.length < 1 ? (
                <tr>
                  <td className="px-6 py-4" colSpan={6}>
                    No recent orders
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order, index) => (
                  <tr key={order.index} className="h-14 text-[14px] border-b">
                    <td className="px-6 py-4">{order.index}</td>
                    <td className="px-6 py-4">{order.id}</td>
                    <td className="px-6 py-4">
                      {order.order_type === "Refill" ? (
                        <RefillOrder />
                      ) : (
                        <NewOrder />
                      )}
                    </td>
                    {order.steps.map((step) => (
                      <td
                        className="px-1 py-2 capitalize w-[40px]"
                        key={step.step}
                      >
                        {step.is_done ? (
                          <CheckCheck size={16} color="green" />
                        ) : (
                          "-"
                        )}
                      </td>
                    ))}
                    <td className="px-6 py-4">
                      {
                        new Date(order.created_at)
                          .toLocaleString()
                          .split(",")[0]
                      }
                    </td>
                    <td
                      className="px-6 py-4 underline text-primary cursor-pointer"
                      onClick={() =>
                        navigate(`/dashboard/patients/${id}/orders/${order.id}`)
                      }
                    >
                      View order
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Outlet />
    </div>
      <Tooltip
        content="Intake"
        place="top"
        effect="solid"
        id="intake"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="PV1"
        place="top"
        effect="solid"
        id="pv1"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="Fulfillment"
        place="top"
        effect="solid"
        id="fulfillment"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="PV2"
        place="top"
        effect="solid"
        id="pv2"
        className="!text-xs !font-normal"
      />
      <Tooltip
        content="Shipping"
        place="top"
        effect="solid"
        id="shipping"
        className="!text-xs !font-normal"
      />
    </>
  );
};

export default OrderPage;
